import { User } from "@/models/auth";
import { RightColumnModule as StockShortsRightColumnModule } from "@/models/stock-shorts";
import { ModuleTemplate, TabTemplate } from "@/models/custom-views";
import { Universe, UserUniverses } from "@/models/universe";
import { isUndefined } from "lodash";
import { atom, PrimitiveAtom, SetStateAction } from "jotai";
import { Instrument } from "@/models/instruments";
import { Notification } from "@/models/notifications";
import { Plan, Subscription } from "@/models/subscription";
import { brighten } from "./ortex-charts/color";
import { NEGATIVE_DEFAULT, NEGATIVE_LIGHT_DEFAULT, resolveNegativeColor } from "./negativeColor";
import { timeSettingsState } from "./time/state";
import { normaliseTimeSettings } from "./time/zone";

/**
 * A writable atom that starts out empty. Jotai types `atom(x)` as a read-only derived atom whenever
 * `x` could be a read function, and with `strict: false` that includes `null` and `undefined`; this
 * is plain `atom(initial)` at runtime, typed as the writable atom it is.
 */
const emptyAtom = <T>(initial: null | undefined) => atom(initial as T) as PrimitiveAtom<T>;

// Dark until the saved choice is read, which is what the client picks for anyone without one: the
// server used to render with no theme (white), so a server-rendered page could not be shown before
// hydration without a flash of the wrong colours (src/lib/loaderHold.ts).
export const themeState = atom<"dark" | "light">("dark");

// Display contrast (normal | high). High contrast is a dark-mode modifier:
// redesign.scss swaps the CSS tokens, and _app.tsx swaps the colour atoms
// below so charts and inline-painted colours follow too.
// Alt Data upgrade dialog (Visitor / Free / Basic hitting an Alt Data entry point).
export const altDataUpgradeDialogState = atom<boolean>(false);

export const contrastState = atom<"normal" | "high">("normal");

// Colours the account has saved (raw values from the settings payload).
// Undefined = never saved. Used so manual colour choices win in every display
// mode, including high contrast.
export type UserColorKey = "positive" | "negative" | "grey" | "blackMedium";
export const userColorSettingsState = atom<Partial<Record<UserColorKey, string>>>({});

// Every default a colour setting has ever had. A saved value equal to one of
// these is not a manual choice (the settings page persists defaults too).
const COLOR_DEFAULT_HISTORY: Record<UserColorKey, string[]> = {
    positive: ["rgb(49,171,166)", "rgb(53,196,138)", "rgb(30,160,105)"],
    negative: ["rgb(239,68,56)", "rgb(239,90,106)", "rgb(226,66,80)", NEGATIVE_DEFAULT, NEGATIVE_LIGHT_DEFAULT],
    grey: ["rgb(128,128,128)", "rgb(151,161,178)", "rgb(161,166,173)"],
    blackMedium: ["rgb(21,30,35)", "rgb(13,17,26)", "rgb(22,24,27)"],
};
const normalizeColor = (c: string) => c.replace(/\s+/g, "").toLowerCase();
export const isManualColor = (key: UserColorKey, value: string | undefined): boolean => {
    if (!value) return false;
    const v = normalizeColor(value);
    return !COLOR_DEFAULT_HISTORY[key].some((d) => normalizeColor(d) === v);
};

export const HIGH_CONTRAST_COLORS = {
    ortexColor: "rgb(46, 230, 223)",
    lighterOrtexColor: "rgba(46, 230, 223, 0.7)",
    positive: "rgb(0, 255, 136)",
    negative: "rgb(255, 77, 77)",
    grey: "rgb(208, 208, 208)",
    blackMedium: "rgb(0, 0, 0)",
    darkDefault: "rgb(0, 0, 0)",
    darkSurfaceDefault: "rgb(16, 16, 16)",
    iconColor: "rgb(208, 208, 208)",
    dividerDark: "rgb(58, 58, 58)",
    borderDark: "rgb(90, 90, 90)",
    popoverContent: "rgb(16, 16, 16)",
};

export const universeState = emptyAtom<Universe>(undefined);

export const universesState = emptyAtom<UserUniverses>(undefined);

export const regionState = emptyAtom<7 | 10>(undefined);

export const ortexColorState = atom<string>("rgb(49, 171, 166)");

export const lighterOrtexColorState = atom<string>("rgba(49, 171, 166, 0.7)");

export const newsOptionsState = atom({});

export const positiveState = atom<string>("rgb(30,160,105)");

export const positiveGradientState = atom<string>((get) => {
    const positiveColor = get(positiveState);

    return "linear-gradient(270deg," + positiveColor + " 0%, transparent 100%)";
});

/** The negative colour as the reader set it (or the default); what Settings saves. */
export const negativeSettingState = atom<string>(NEGATIVE_DEFAULT);

/**
 * The negative colour the UI draws with (see negativeColor.ts): the dark default is 5.1:1 on the dark panels but
 * 3.5:1 on white, so light mode uses a darker red (#c93c48, 5.0:1 on white) unless the reader chose
 * their own. Writes go to the setting, like the grey in subtextGreyState.
 */
export const negativeState = atom<string, [string], void>(
    (get) => resolveNegativeColor(get(themeState), get(negativeSettingState), isManualColor("negative", get(negativeSettingState))),
    (_get, set, value) => set(negativeSettingState, value)
);

export const negativeGradientState = atom<string>((get) => {
    const negativeColor = get(negativeState);

    return "linear-gradient(90deg," + negativeColor + " 0%, transparent 100%)";
});

export const neutralState = atom<string>("rgb(171,133,109)");

export const greyState = atom<string>("rgb(161,166,173)");

// A grid cell's ticker line and sub-text: the default grey is 2.4:1 on white, so light mode uses the
// secondary text token (6.7:1) unless the reader picked their own grey.
export const subtextGreyState = atom<string>((get) => (get(themeState) === "light" && !isManualColor("grey", get(userColorSettingsState).grey) ? "var(--text-2, #575c63)" : get(greyState)));

export const blackState = atom<string>("rgb(0,0,0)");

export const whiteState = atom<string>("rgb(255,255,255)");

export const blackMediumState = atom<string>("rgb(22, 24, 27)");

export const darkDefaultState = atom<string>("rgb(15, 16, 18)");

export const darkSurfaceDefaultState = atom<string>("rgb(22, 24, 27)");

export const whiteMediumState = atom<string>("rgb(244, 245, 247)");

export const iconColorState = atom<string>("rgb(161, 166, 173)");

export const dividerDarkState = atom<string>("rgb(38, 41, 46)");

export const dividerLightState = atom<string>("rgb(227,229,233)");

export const borderDarkState = atom<string>("rgb(54, 58, 65)");

export const borderLightState = atom<string>("rgb(207, 211, 217)");

export const dividerState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(dividerDarkState) : get(dividerLightState);
});

export const borderColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(borderDarkState) : get(borderLightState);
});

export const greyLighterState = atom<string>((get) => brighten(get(greyState), 0.09));

export const greyMediumState = atom<string>((get) => brighten(get(greyState), -0.16));

export const greyDarkerState = atom<string>((get) => brighten(get(greyState), -0.26));

export const blackDarkerState = atom<string>((get) => brighten(get(blackMediumState), -0.03));

export const blackLighterState = atom<string>((get) => {
    const blackMedium = brighten(get(blackMediumState), 0.07);
    const rgb = blackMedium.match(/[0-9]+/g);
    rgb[0] = String(Math.max(0, Number(rgb[0]) - 8));
    rgb[2] = String(Math.min(255, Number(rgb[2]) + 8));
    return `rgb(${rgb.join(",")})`;
});

export const whiteLighterState = atom<string>((get) => brighten(get(whiteMediumState), 0.05));

export const whiteDarkerState = atom<string>((get) => brighten(get(whiteMediumState), -0.03));

export const textColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? brighten(get(whiteState), -0.1) : get(blackState);
});

export const backgroundColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(blackMediumState) : get(whiteDarkerState);
});

export const newbackgroundColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(darkDefaultState) : get(whiteState);
});

export const darkerBackgroundColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(darkDefaultState) : get(whiteDarkerState);
});

export const lighterBackgroundColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(blackLighterState) : get(whiteLighterState);
});

export const tabLinksColorState = atom<string>((get) => {
    const theme = get(themeState);
    return theme === "dark" ? get(whiteState) : get(blackState);
});

export const newsAutocompleteState = emptyAtom<any>(undefined);

export const filterChipsState = atom([]);

export const popoverContentState = atom<string>("rgb(29, 32, 36)");

export const userDataState = emptyAtom<User>(undefined);

/**
 * True once the `/API/v2/user` call has given a definitive answer: a payload (visitors get one
 * too) or a definite 401/403 (no user). False before that and while a transient failure is
 * retried. Set by useGetUserData. Lets callers tell "no answer yet" from "answered: no"
 * without inferring it from fields of the user object.
 */
export const userPayloadSettledState = atom(false);

export const agreementsState = emptyAtom<{ code: string }[]>(undefined);

export const moduleSettingsState = atom<{ [key: string]: any }>({});

export const stockShortsExpandedState = atom<StockShortsRightColumnModule>("short_shares");
export const searchSettingsState = atom<any[]>([]);

export const tickerTapeState = atom<{ [identifier: string]: Instrument }>({});

export const hideTickertapeState = atom<boolean>(true);

export const customViewsState = atom<TabTemplate[]>([]);

export const customViewsUnsavedState = atom<boolean>(false);

export const customViewsActiveTabState = emptyAtom<number>(null);

export const customViewsCurrentViewDataState = atom<TabTemplate[]>([]);

export const customViewsEditModeState = atom<boolean>(false);

export const customViewsSpeedDialState = atom<boolean>(false);

export const customViewsCompactTypeState = atom<"horizontal" | "vertical" | null>("horizontal");

export const customViewsPreventCollisionState = atom<boolean>(false);

export const customViewsDraggableItemState = emptyAtom<ModuleTemplate>(undefined);

export const customViewsComponentsState = atom<ModuleTemplate[]>([]);

// The user object put together from the atoms below, and written back to them. A derived atom keeps
// only its latest value (Recoil's selector cache kept every past combination of inputs, so each
// settings save left another whole user object in memory until it was set to most-recent).
export const userState = atom<User, [SetStateAction<User>], void>(
    (get) => ({
        ...get(userDataState),
        settings: {
            theme: get(themeState),
            region: get(regionState),
            universe: get(universeState),
            positive: get(positiveState),
            negative: get(negativeSettingState),
            neutral: get(neutralState),
            grey: get(greyState),
            black: get(blackState),
            white: get(whiteState),
            blackMedium: get(blackMediumState),
            whiteMedium: get(whiteMediumState),
            module_settings: get(moduleSettingsState),
            agreements: get(agreementsState),
            stock_shorts_expanded: get(stockShortsExpandedState),
            search_settings: get(searchSettingsState),
            time: get(timeSettingsState),
        },
    }),
    (get, set, update) => {
        // Recoil handed a selector's setter the resolved value; Jotai passes `setUser(prev => ...)`
        // through as a function, so it is resolved here against the current user.
        const value = typeof update === "function" ? update(get(userState)) : update;
        const user = { ...value };
        delete user.settings;
        set(userDataState, value ? user : null);
        if (value) {
            if (value.settings.theme) set(themeState, value.settings.theme);
            set(universeState, value.settings.universe || { id: 7, name: "US & Canada", US_stocks: true });
            set(regionState, isUndefined(value.settings.region) ? 7 : value.settings.region);
            set(userColorSettingsState, {
                positive: value.settings.positive,
                negative: value.settings.negative,
                grey: value.settings.grey,
                blackMedium: value.settings.blackMedium,
            });
            set(positiveState, isUndefined(value.settings.positive) ? "rgb(30,160,105)" : value.settings.positive);
            set(negativeSettingState, isUndefined(value.settings.negative) ? NEGATIVE_DEFAULT : value.settings.negative);
            set(neutralState, isUndefined(value.settings.neutral) ? "rgb(171,133,109)" : value.settings.neutral);
            set(greyState, isUndefined(value.settings.grey) ? "rgb(161,166,173)" : value.settings.grey);
            set(blackState, isUndefined(value.settings.black) ? "rgb(0,0,0)" : value.settings.black);
            set(whiteState, isUndefined(value.settings.white) ? "rgb(255,255,255)" : value.settings.white);
            set(blackMediumState, isUndefined(value.settings.blackMedium) ? "rgb(22, 24, 27)" : value.settings.blackMedium);
            set(whiteMediumState, isUndefined(value.settings.whiteMedium) ? "rgb(244, 245, 247)" : value.settings.whiteMedium);
            set(moduleSettingsState, isUndefined(value.settings.module_settings) ? {} : value.settings.module_settings);
            set(agreementsState, isUndefined(value.settings.agreements) ? [] : value.settings.agreements);
            set(stockShortsExpandedState, isUndefined(value.settings.stock_shorts_expanded) ? "short_shares" : value.settings.stock_shorts_expanded);
            set(searchSettingsState, isUndefined(value.settings.search_settings) ? [] : value.settings.search_settings);
            set(timeSettingsState, normaliseTimeSettings(value.settings.time));
        }
    }
);

export const clearFiltersState = atom(false);

export const subscriptionOfferingsState = emptyAtom<any>(null);

export const appUpgradeLoadingState = atom(false);

export const subscriptionState = emptyAtom<Subscription>(null);

export const planState = emptyAtom<Plan>(null);

export const codeAppliedState = atom<string>("");

export const promoCodeDisabledState = atom<boolean>(true);

export const paymentDrawerOpenState = atom(false);

export const planPickerDrawerOpenState = atom(false);

export const planPickerSelectedPackageState = emptyAtom<any>(null);

export const paymentModalOpenState = atom(false);

export const showAppSubscriberModalState = atom(false);

export const notificationsListState = atom<Notification[]>([]);

export const stockTradingItemIdState = emptyAtom<number>(null);

// Stock Report existence — set by RootLayout when the stock page
// mounts (single shared fetch), read by StockLinks (tab insertion +
// "New" badge) and the top-bar badge (in RootLayout itself). Keyed
// to the current stock so a navigation that updates the page also
// updates this; null means "not yet resolved", undefined means
// "resolved and there is no report".
export interface StockReportAvailability {
    exists: boolean;
    headline?: string | null;
    generatedAt?: string | null;
    deepLinkPath?: string | null;
}
export const stockReportAvailabilityState = emptyAtom<StockReportAvailability | null>(null);

// Trading Idea existence for the current stock — same shape and same
// lifecycle as the Stock Report atom above, set by RootLayout on mount.
//
// The backend probe is GATED: a user without the product group is told
// `exists: false`, indistinguishable from a stock with no idea. So a
// falsy value here means "render nothing", never "render an empty tab"
// — an empty Trading Idea tab would advertise the product AND leak that
// ORTEX holds a published directional view on this particular name.
export interface TradingIdeaAvailability {
    exists: boolean;
    id?: number;
    headline?: string | null;
    direction?: "long" | "short";
    publishedAt?: string | null;
    deepLinkPath?: string | null;
}
export const tradingIdeaAvailabilityState = emptyAtom<TradingIdeaAvailability | null>(null);

export const showAgreementsModalState = atom(false);

export const alertsListState = atom<any[]>([]);

export const tickerWSState = atom<string[]>([]);

export const filtersDrawerOpenState = atom(false);

export const chatMinimizedState = atom<boolean>(false);

export const chatModalState = atom<{
    open: boolean;
    initialMessage?: string;
    startingModel?: string;
    chatModel?: string;
}>({
    open: false,
    initialMessage: undefined,
    startingModel: undefined,
    chatModel: undefined,
});

export const chatModalOpenState = atom((get) => get(chatModalState).open);

// Phone push notifications (ortex/ted/ted#2485). Only ever set inside the ORTEX app.
// Token: the FCM token this device registered with (null until registration succeeds).
export const pushTokenState = emptyAtom<string | null>(null);

// OS notification permission as last read from the PushNotifications plugin.
// "unsupported" on desktop web / mobile web / app builds without the plugin.
export type PushPermission = "granted" | "denied" | "prompt" | "unsupported";
export const pushPermissionState = atom<PushPermission>("unsupported");

// The "Turn on notifications" pre-prompt. reason "launch" respects the 7-day snooze;
// "alert" is the contextual offer after creating an alert with Push delivery.
export const pushPrePromptState = atom<{ open: boolean; reason: "launch" | "alert" }>({ open: false, reason: "launch" });

// True while a signed-out visitor is on the page of a stock that is free today (ted #2647).
// Set by LockedValuePanel; the padlocks (useUpgradeCta) then offer a free account, not Advanced.
export const freeStockTodayState = atom<boolean>(false);
